import React, { memo, useEffect, useState } from 'react'

import { Button, Icon } from '@/shared/components'
import { Dropdown } from '@/shared/components/Dropdown/Dropdown'
import { DropdownItem } from '@/shared/components/Dropdown/DropdownItem'
import { useWidth } from '@/shared/lib/useWidth'
import { pxToRem } from '@/shared/styles/themes'
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'
import { Box, Divider, Grid2 as Grid, Slider, styled, Typography } from '@mui/material'

import { FilterMultiselectDropdown } from '../FilterMultiselectDropdown/FilterMultiselectDropdown'
import { SelectDropdown } from '../SelectDropdown/SelectDropdown'
import { MosaicToolbarMenuItem } from './models/MosaicToolbarMenuItem'

export type MosaicAdditionalToolbarProps = {
  toolbarMenuItems: MosaicToolbarMenuItem[]
  averageMenuItemWidth?: number //default 120
  children?: React.ReactNode
}

const InternalMosaicAdditionalToolbar: React.FC<MosaicAdditionalToolbarProps> = ({ toolbarMenuItems, children, averageMenuItemWidth = 120 }) => {
  const { ref: containerRef, width: containerWidth } = useWidth()
  const [visibleMenuItems, setVisibleMenuItems] = useState<MosaicToolbarMenuItem[]>(toolbarMenuItems)
  const [overflowMenuItems, setOverflowMenuItems] = useState<MosaicToolbarMenuItem[]>([])

  useEffect(() => {
    let cumulativeWidth = 0
    const averageWidth = averageMenuItemWidth
    const newvisibleMenuItems: MosaicToolbarMenuItem[] = []
    const newoverflowMenuItems: MosaicToolbarMenuItem[] = []

    toolbarMenuItems.forEach((menuItem) => {
      cumulativeWidth += averageWidth

      if (cumulativeWidth <= containerWidth) {
        newvisibleMenuItems.push(menuItem)
      } else {
        newoverflowMenuItems.push(menuItem)
      }
    })

    setVisibleMenuItems(newvisibleMenuItems)
    setOverflowMenuItems(newoverflowMenuItems)
  }, [containerWidth, setVisibleMenuItems, setOverflowMenuItems, averageMenuItemWidth, toolbarMenuItems])


  const renderMenuItem = (item: MosaicToolbarMenuItem) => {
    if (item.type === 'actionItem') {
      return (
        <StyledActionButton
          text
          disabled={item.disabled}
          tooltipTitle={item.tooltip || item.label}
          startIcon={item.icon ? <Icon icon={item.icon} /> : undefined}
          onClick={item.onClick}
        >
          {item.label}
        </StyledActionButton>
      )
    } else if (item.type === 'separator') {
      return <Divider orientation="vertical" flexItem />
    } else if (item.type === 'dropdown') {
      return (
        <SelectDropdown
          options={item.options}
          value={item.value}
          disabled={item.disabled}
          onChange={(_, value) => item.onChange(value)}
        />
      )
    } else if (item.type === 'multidropdown') {
      return (
        <FilterMultiselectDropdown
          options={item.options}
          values={item.values}
          disabled={item.disabled}
          onSelectItem={item.onSelectItem}
          searchActionEnable={item.searchActionEnable}
          searchActionIcon={item.searchActionIcon}
          slotProps={item.slotProps}
          label={item.label}
          sx={{ minWidth: 120 }}
        />
      )
    } else if (item.type === 'slider') {
      return (
        <Box sx={{
          display: 'inline-flex',
          minWidth: '180px',
          alignItems: 'center',
          gap: '2px',
        }}>
          <Typography variant="body2">{item.label}:</Typography>
          <Slider
            value={item.value}
            min={item.min}
            max={item.max}
            step={item.step}
            onChange={item.onChange}
            aria-label={item.label}
          />
        </Box>
      )
    }

    return null
  }


  const renderOverflowMenuItem = (item: MosaicToolbarMenuItem, key: React.Key) => {
    if (item.type === 'actionItem') {
      return (
        <DropdownItem key={key} disabled={item.disabled} onSelect={item.onClick}>
          <Icon icon={item.icon} />
          {item.label}
        </DropdownItem>
      )
    } else if (item.type === 'separator') {
      return null
    } else if (item.type === 'dropdown') {
      return (
        <DropdownItem key={key}>
          <SelectDropdown
            options={item.options}
            value={item.value}
            disabled={item.disabled}
            onChange={(_, value) => item.onChange(value)}
          />
        </DropdownItem>
      )
    } else if (item.type === 'multidropdown') {
      return (
        <DropdownItem key={key}>
          <FilterMultiselectDropdown
            options={item.options}
            values={item.values}
            disabled={item.disabled}
            onSelectItem={item.onSelectItem}
            searchActionEnable={item.searchActionEnable}
            searchActionIcon={item.searchActionIcon}
            slotProps={item.slotProps}
            label={item.label}
            sx={{ minWidth: 120 }}
          />
        </DropdownItem>
      )
    }

    return null
  }

  // Separate left and right floating items
  const leftItems = visibleMenuItems.filter(item => item.float !== 'right')
  const rightItems = visibleMenuItems.filter(item => item.float === 'right')
  const needsSpacer = leftItems.length > 0 && rightItems.length > 0

  return (
    <StyledContainer ref={containerRef} container direction={'row'} alignItems={'center'}>
      {leftItems.map((item, idx) => (
        <Grid key={`left-${idx}`}>
          {renderMenuItem(item)}
        </Grid>
      ))}
      
      {needsSpacer && (
        <Grid sx={{ flexGrow: 1 }} />
      )}
      
      {rightItems.map((item, idx) => (
        <Grid key={`right-${idx}`} sx={{ marginRight: pxToRem(8) }}>
          {renderMenuItem(item)}
        </Grid>
      ))}
      
      <Grid>
        {children}
      </Grid>
      
      {overflowMenuItems.length > 0 && (
        <Dropdown
          icon={<StyledActionsIcon icon={faEllipsisVertical} />}
          value="Actions"
          isIconButton
          slotProps={{
            iconButton: {
              round: true,
              sx: { margin: 0 },
            },
          }}
        >
          {overflowMenuItems.map((actionItem, idx) => renderOverflowMenuItem(actionItem, idx))}
        </Dropdown>
      )}
    </StyledContainer>
  )
}

export const MosaicAdditionalToolbar = memo(InternalMosaicAdditionalToolbar, (prevProps, nextProps) => {
  return prevProps.toolbarMenuItems === nextProps.toolbarMenuItems
})

const StyledActionsIcon = styled(Icon)`
  font-size: ${(props) => props.theme.typography.pxToRem(16)};
`

const StyledContainer = styled(Grid)`
  width: 100%;
  height: 35px;
  padding-top: 1px;
  background-color: ${(props) => props.theme.palette.background.paper};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`

const StyledActionButton = styled(Button)`

`